import { FC, ReactNode } from 'react'

import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded'
import { Box, IconButton } from '@mui/material'

import { CreativeAdNetworkSelector } from '../CreativeAdNetworkSelector/CreativeAdNetworkSelector'
import { UAInsightsMonthSelector } from '../UAInsightsMonthSelector/UAInsightsMonthSelector'

interface UAInsightsPageHeaderProps {
  /** Collapsed state from `useStickyHeader`, driven by scroll position. */
  compact: boolean
  onBack: () => void
  /** Accessible label for the back button — each page returns somewhere different. */
  backLabel: string
  /**
   * Who the page is about: the game identity on the game page, the
   * source-vs-target matchup on the Compare page. Rendered between the back
   * button and the scope selectors, and expected to flex into the free space.
   */
  identity: ReactNode
  /** Page-specific controls rendered after the network / month selectors. */
  actions?: ReactNode
  /**
   * Tab strip for the page. Always gets its own full-width row below the
   * title row, in both the expanded and compact states: a dedicated row lets
   * the strip grow as tabs are added instead of fighting the identity and
   * selectors for space, and the scrollable variant absorbs overflow on
   * narrow viewports.
   */
  tabs?: ReactNode
  /**
   * Disables the scope selectors during the first fetch. Deliberately not
   * applied on later refetches — the selectors are what trigger those, so
   * disabling them would lock the user out mid-interaction.
   */
  disabled?: boolean
}

/**
 * The sticky page header shared by the UA Insights game page and Compare page:
 * back button, identity block, network + month selectors, and a tab row that
 * collapses on scroll.
 *
 * The `data-ua-insights-sticky-header` marker is part of the contract — the
 * creative-tag deep-link scroll measures this element's on-screen bottom to
 * land a tag card just below the pinned header rather than behind it.
 */
export const UAInsightsPageHeader: FC<UAInsightsPageHeaderProps> = ({ compact, onBack, backLabel, identity, actions, tabs, disabled }) => (
  <Box
    data-ua-insights-sticky-header
    sx={{
      position: 'sticky',
      // Sit below the global AppHeader (height defined in _variables.scss as $app-header-height).
      top: '95px',
      zIndex: 50,
      isolation: 'isolate',
      bgcolor: 'background.default',
      pt: compact ? 0.5 : 1.5,
      mt: -2.3,
      mb: 2,
      width: '100%',
      borderBottom: '1px solid',
      borderColor: 'divider',
      // Smooth the size transition so the collapse doesn't snap.
      transition: 'padding-top 160ms ease',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 1 : 0.5,
        pb: compact ? 0.5 : 1.5,
        transition: 'padding-bottom 160ms ease, gap 160ms ease',
      }}
    >
      <IconButton onClick={onBack} color="primary" aria-label={backLabel} size={compact ? 'small' : 'medium'} sx={{ flexShrink: 0 }}>
        <ChevronLeftRounded />
      </IconButton>
      {identity}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        <CreativeAdNetworkSelector sx={{ flexShrink: 0 }} disabled={disabled} />
        <UAInsightsMonthSelector sx={{ flexShrink: 0 }} disabled={disabled} />
        {actions}
      </Box>
    </Box>
    {tabs}
  </Box>
)
